import {
    logExperiment as abacusLogExperiment,
    evaluateExperiment as abacusEvaluateExperiment
} from '@homeaway/abacus-client-utils';
import {evaluationIsVariant} from '@homeaway/react-experiment';

/**
 * This is a doppelganger of the Experiment component, meant to mimic its interfact.
 * @param {object} abacus - The abucus test matrix; required only for ssr tests
 * @param {string} name - The experiment's name
 * @param {number|array} [variant=0] - The target variant.
 * @param {boolean} [deferLogging=false] - To not log the result. Default is to log.
 * @return {boolean}
 */
function evaluateExperiment({abacus = null, name = 'INVALID', variant = 0, deferLogging = false}) {
    const abacusData = abacus || (typeof window !== 'undefined' && window.__ABACUS__) || {};
    const inExperiment = evaluationIsVariant(abacusData[name], variant);

    if (!deferLogging) {
        abacusLogExperiment(abacusData[name]);
    }

    return inExperiment;
}

/*
    THE FOLLOWING ABACUS TEST HELPERS ARE DEPRECATED!!!
    Please use the previous evaluateExperiment, since it uses shared logic in react-experiment,
    which in turn is shared in the components.


____    _____   ____    ____    _____    ____      _      _____   _____   ____
|  _ \  | ____| |  _ \  |  _ \  | ____|  / ___|    / \    |_   _| | ____| |  _ \
| | | | |  _|   | |_) | | |_) | |  _|   | |       / _ \     | |   |  _|   | | | |
| |_| | | |___  |  __/  |  _ <  | |___  | |___   / ___ \    | |   | |___  | |_| |
|____/  |_____| |_|     |_| \_\ |_____|  \____| /_/   \_\   |_|   |_____| |____/

    OLD NOTES:
    Abacus Test Helpers Notes.
    These functions are for the simple abacus logic that can either be used directly, or through
    generalized components.

    The Abacus Test Utils have a requirement of using the window.__ABACUS__ data
    by default, and require instanciating a new instance for other cases. They then fall back to odd
    values, and that should be addressed, perhaps.

    So, while hammering out these Abacus patterns, I chose to make these utils instead
    of having continual PRs against that other repo.

    These functions follow a pattern.  There is a 'normal' version of the function
    that uses the window.__ABACUS__ data, and then another version, usually called
    ---InData, that uses a given abacus test matrix for the first parameter.

    The reason is that server side rendering will not have the data in the global
    window, and a generalized window agnostic version of the function exists to hold
    any key logic.  However, it is more appropriately named.
 */

/**
 * Wrap the abacus test evaluator, and extended logic, that is common boilerplate.
 * Currently this assumes we know the variant if we want to log.
 * @param {object} bucketData - The data matrix returned from Abacus
 * @param {string} bucketName - The test name to evaluate.
 * @param {number|array} [variant=-1] - The target variant.  -1 (defaults) evaluates for all variants.
 * @param {boolean} [logEvaluation=false] - To execute the logging of a successful test.
 * @return {boolean}
 ____    _____   ____    ____    _____    ____      _      _____   _____   ____
|  _ \  | ____| |  _ \  |  _ \  | ____|  / ___|    / \    |_   _| | ____| |  _ \
| | | | |  _|   | |_) | | |_) | |  _|   | |       / _ \     | |   |  _|   | | | |
| |_| | | |___  |  __/  |  _ <  | |___  | |___   / ___ \    | |   | |___  | |_| |
|____/  |_____| |_|     |_| \_\ |_____|  \____| /_/   \_\   |_|   |_____| |____/

 */
/* eslint-disable complexity */
function isInBucketData(bucketData, bucketName, variant = -1, logEvaluation = false) {
    // evaluateExperiment just returns the bucket data from the global __ABACUS__
    const testData = bucketData[bucketName];

    if (!testData) {
        // If we are asking explicitly for the control variant, and there isn't any
        // test data for various reasons, consider it in the bucket.
        if (variant instanceof Array) {
            return variant.indexOf(0) !== -1;
        } else if (variant === 0) {
            return true;
        }

        // This means the test is either not live, or something is wrong.
        return false;
    }

    let isInTest = false;
    if (variant instanceof Array) {
        isInTest = variant.indexOf(testData.value) !== -1;
    } else if (variant >= 0) {
        isInTest = testData.value === variant;
    } else {
        isInTest = !!testData.value;
    }

    // This is kind of an experimental part.  We generally log from actions, but
    // we can, and perhaps should, log from here since shared components will.
    if (logEvaluation) {
        abacusLogExperiment(testData);
    }

    return isInTest;
}
/* eslint-enable complexity */

/**
 * This is the client-side 'easy' function to call.
 * It mimics what abacus-client-utils does, and REQUIRES window.__ABACUS__ to be defined.
 * @param {string} bucketName - The test name to evaluate.
 * @param {number|array} [variant=-1] - The target variant.  -1 (defaults) evaluates for all variants.
 * @param {boolean} [logEvaluation=false] - To execute the logging of a successful test.
 * @return {boolean}
 ____    _____   ____    ____    _____    ____      _      _____   _____   ____
|  _ \  | ____| |  _ \  |  _ \  | ____|  / ___|    / \    |_   _| | ____| |  _ \
| | | | |  _|   | |_) | | |_) | |  _|   | |       / _ \     | |   |  _|   | | | |
| |_| | | |___  |  __/  |  _ <  | |___  | |___   / ___ \    | |   | |___  | |_| |
|____/  |_____| |_|     |_| \_\ |_____|  \____| /_/   \_\   |_|   |_____| |____/

 */
function isInBucket(bucketName, variant = -1, logEvaluation = false) {
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return false;
    }

    return isInBucketData(window.__ABACUS__, bucketName, variant, logEvaluation);
}

/*
____    _____   ____    ____    _____    ____      _      _____   _____   ____
|  _ \  | ____| |  _ \  |  _ \  | ____|  / ___|    / \    |_   _| | ____| |  _ \
| | | | |  _|   | |_) | | |_) | |  _|   | |       / _ \     | |   |  _|   | | | |
| |_| | | |___  |  __/  |  _ <  | |___  | |___   / ___ \    | |   | |___  | |_| |
|____/  |_____| |_|     |_| \_\ |_____|  \____| /_/   \_\   |_|   |_____| |____/

 */
function logExperimentFromData(bucketData, bucketName) {
    const testData = bucketData[bucketName];

    if (!testData) {
        // This means the test is either not live, or something is wrong.
        return false;
    }

    return abacusLogExperiment(testData);
}

/**
 * This is the client-side 'easy' function to call.
 * It mimics what abacus-client-utils does, and REQUIRES window.__ABACUS__ to be difined.
 * @param {string} bucketName - The test name to evaluate.
 * @return {boolean}
 ____    _____   ____    ____    _____    ____      _      _____   _____   ____
|  _ \  | ____| |  _ \  |  _ \  | ____|  / ___|    / \    |_   _| | ____| |  _ \
| | | | |  _|   | |_) | | |_) | |  _|   | |       / _ \     | |   |  _|   | | | |
| |_| | | |___  |  __/  |  _ <  | |___  | |___   / ___ \    | |   | |___  | |_| |
|____/  |_____| |_|     |_| \_\ |_____|  \____| /_/   \_\   |_|   |_____| |____/

 */
function logExperiment(bucketName) {
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return false;
    }

    return logExperimentFromData(window.__ABACUS__, bucketName);
}

/** Simple Function to get testData by evaluating an experiment which is active
 * @param {string} bucketName - The test name to evaluate.
 * @return {number, boolean}
 ____    _____   ____    ____    _____    ____      _      _____   _____   ____
|  _ \  | ____| |  _ \  |  _ \  | ____|  / ___|    / \    |_   _| | ____| |  _ \
| | | | |  _|   | |_) | | |_) | |  _|   | |       / _ \     | |   |  _|   | | | |
| |_| | | |___  |  __/  |  _ <  | |___  | |___   / ___ \    | |   | |___  | |_| |
|____/  |_____| |_|     |_| \_\ |_____|  \____| /_/   \_\   |_|   |_____| |____/

 */
function getBucketValue(bucketName) {
    if (typeof window === 'undefined' || !window.__ABACUS__) {
        // Abacus client utils tend to crash if __ABACUS__ is not defined.
        // We shouldn't know what's under the covers, though.
        return false;
    }

    // evaluateExperiment just returns the bucket data from the global __ABACUS__
    const testData = abacusEvaluateExperiment(bucketName);

    if (!testData) {
        // This means the test is either not live, or something is wrong.
        return false;
    }

    return testData.value;
}

export default {
    evaluateExperiment,
    getBucketValue,
    isInBucket,
    isInBucketData,
    logExperiment,
    logExperimentFromData
};

export {
    evaluateExperiment,
    getBucketValue,
    isInBucket,
    isInBucketData,
    logExperiment,
    logExperimentFromData
};
